import React, { useState, useEffect, useRef } from 'react'
import { Box, TextField, Typography, Alert, Grid } from '@mui/material'
import { textFieldStyle } from '../../components/CreateProcessTemplateDialog'

const DEBOUNCE_MS = 300

const PayloadEditor = ({ value, onChange, label = 'Payload' }) => {
  const [jsonString, setJsonString] = useState('')
  const [error, setError] = useState(null)
  const [isValid, setIsValid] = useState(true)

  const textFieldRef = useRef(null)
  const debounceRef = useRef(null)
  const isEditingRef = useRef(false)

  /** Sync external value → editor (only when not typing) */
  useEffect(() => {
    if (isEditingRef.current) return

    if (!value) {
      setJsonString('')
      setError(null)
      setIsValid(true)
      return
    }

    try {
      setJsonString(JSON.stringify(value, null, 2))
      setError(null)
      setIsValid(true)
    } catch {
      setError('Invalid JSON object')
      setIsValid(false)
    }
  }, [value])

  const handleChange = (e) => {
    const val = e.target.value
    setJsonString(val)
    isEditingRef.current = true

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      if (!val.trim()) {
        setError(null)
        setIsValid(true)
        onChange({})
        return
      }

      try {
        const parsed = JSON.parse(val)
        setError(null)
        setIsValid(true)
        onChange(parsed)
      } catch (err) {
        setError(err.message)
        setIsValid(false)
      }
    }, DEBOUNCE_MS)
  }

  const handleBlur = () => {
    isEditingRef.current = false
  }

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>

      <Grid container>
        <Grid item xs={12}>
          <TextField
            ref={textFieldRef}
            sx={textFieldStyle}
            multiline
            rows={8}
            fullWidth
            value={jsonString}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={`{\n  "field_name": "{{field_value}}",\n  "static_field": "static_value"\n}`}
            error={!isValid}
            helperText={
              error
                ? `Invalid JSON: ${error}`
                : ''
            }
            InputProps={{
              style: {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />

          {isValid && jsonString && !error && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Valid JSON
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default PayloadEditor
